import { faPencil } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useState, useEffect, useRef, useMemo } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API_URL } from "../../config";
import { showToast } from "../../components/toast.jsx";
import LoadingSpinner from "../../components/LoadingSpinner.jsx";
import { PermissionWrapper, hasPermission } from "../../helpers/permission.js";

const ManageServicesSeo = () => {
  const managePage = "Manage Services Seo";
  const addPage = "Seo/Services/edit";
  const toastShownRef = useRef(false); // ✅ Prevent duplicate toast

  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isFiltering, setIsFiltering] = useState(false);


  // Search filter states
  const [pageNameFilter, setPageNameFilter] = useState("");
  const [titleFilter, setTitleFilter] = useState("");
  const [descriptionFilter, setDescriptionFilter] = useState("");

  // Pagination states
  const [page, setPage] = useState(1);
  const [pageCount, setPageCount] = useState(1);
  const [total, setTotal] = useState(0);
  const limit = 100;

  // 🏷️ Review Type Toggle
  const [reviewType, setReviewType] = useState("streetgains"); // "streetgains" (0) or "streetfolio" (1)

  // Get token from localStorage
  const getToken = () => {
    return localStorage.getItem("authToken");
  };

  // Toggle between review types
  const toggleReviewType = (type) => {
    setReviewType(type);
    setPageNameFilter("");
    setTitleFilter("");
    setDescriptionFilter("");
    setPage(1);
  };

  useEffect(() => {
    const fetchServices = async () => {
      try {
        setLoading(true);
        const token = getToken();
        const serviceType = reviewType === "streetfolio" ? 1 : 0;

        const response = await axios.get(`${API_URL}seoService?service_type=${serviceType}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        if (response.data?.services?.success) {
          setServices(response.data.services.result.servicelist);
          setTotal(response.data.services.result.servicelist.length);
          setPageCount(
            Math.ceil(response.data.services.result.servicelist.length / limit)
          );
          if (!toastShownRef.current) {
            showToast.success("Services fetched successfully.", "success");
            toastShownRef.current = true;
          }
        } else {
          if (!toastShownRef.current) {
            showToast.error("Failed to fetch services.", "error");
            toastShownRef.current = true;
          }
          console.error(
            "Failed to fetch services:",
            response.data?.services?.message
          );
        }
      } catch (error) {
        if (!toastShownRef.current) {
          showToast.error("Failed to fetch services.", "error");
          toastShownRef.current = true;
        }
        console.error("Error fetching services:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchServices();
  }, [reviewType]);

  // Filter services based on search criteria
  const filteredServices = useMemo(() => {
    return services.filter((service) => {
      const serviceName = service?.service_name || "";
      const seoTitle = service?.seo_title || "";
      const seoDesc = service?.seo_desc || "";

      // Page Name filter
      if (
        pageNameFilter &&
        !serviceName.toLowerCase().includes(pageNameFilter.toLowerCase())
      ) {
        return false;
      }

      // Title filter
      if (
        titleFilter &&
        !seoTitle.toLowerCase().includes(titleFilter.toLowerCase())
      ) {
        return false;
      }

      // Description filter
      if (
        descriptionFilter &&
        !seoDesc.toLowerCase().includes(descriptionFilter.toLowerCase())
      ) {
        return false;
      }

      return true;
    });
  }, [services, pageNameFilter, titleFilter, descriptionFilter]);

  // Update pagination based on filtered results
  useEffect(() => {
    setTotal(filteredServices.length);
    setPageCount(Math.ceil(filteredServices.length / limit));
    setPage(1); // Reset to first page when filters change
  }, [filteredServices, limit]);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pageCount) {
      setPage(newPage);
    }
  };

  const start = (page - 1) * limit + 1;
  const end = Math.min(page * limit, total);
  const paginatedServices = filteredServices.slice(start - 1, end);

  const handleEdit = (id) => {
    console.log(`Editing service with id: ${id}`);
  };

  useEffect(() => {
    const handleClearFilters = () => {
      setPageNameFilter("");
      setTitleFilter("");
      setDescriptionFilter("");
    };
    window.addEventListener('clearServiceFilterEvent', handleClearFilters);

    return () => {
      window.removeEventListener('clearServiceFilterEvent', handleClearFilters);
    };
  }, []);

  // Show skeleton when filtering
  useEffect(() => {
    if (!loading) { // Don't trigger if initial loading is still active
      setIsFiltering(true);
      const timer = setTimeout(() => {
        setIsFiltering(false);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [pageNameFilter, titleFilter, descriptionFilter]);

  return (
    <div className="page-container page-header-fixed page-sidebar-fixed fade in mt-5">
      {/* {loading && <LoadingSpinner />} */}
      <div className="">
        <div className="panel panel-default with-rounded-corner with-shadow p-10">
          <div className="panel-heading border-0">
            <div className="row">
              <div className="col-md-6">
                <h4 className="panel-title pull-left">{managePage}</h4>
              </div>
              <div className="col-md-6" style={{ textAlign: "right" }}>
                {/* Pagination Controls */}
                {services.length > 0 && (
                  <div className="d-inline-block">
                    <span style={{ fontSize: "0.8rem" }} className="me-2">
                      {start} to {end} of {total}
                    </span>
                    <button
                      className="btn btn-default btn-sm"
                      title="Previous"
                      onClick={() => handlePageChange(page - 1)}
                      disabled={page === 1}
                    >
                      &lt;
                    </button>
                    &nbsp;
                    <button
                      className="btn btn-default btn-sm"
                      title="Next"
                      onClick={() => handlePageChange(page + 1)}
                      disabled={page === pageCount}
                    >
                      &gt;
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Review Type Toggle */}
          <div className="panel panel-default with-rounded-corner with-shadow mb-3">
            <div className="panel-body">
              <div className="row">
                <div className="col-md-12">
                  <label className="me-3">Select Service Type:</label>
                  <div className="btn-group" role="group">
                    {/* {hasPermission("seo_credits", "view") && ( */}
                    <button
                      type="button"
                      className={`btn ${reviewType === "streetgains" ? "btn-primary" : "btn-default"}`}
                      onClick={() => toggleReviewType("streetgains")}
                    >
                      Streetgains Services
                    </button>
                    {/* )} */}
                    {/* {hasPermission("seo_portfolio", "view") && ( */}
                    <button
                      type="button"
                      className={`btn ${reviewType === "streetfolio" ? "btn-primary" : "btn-default"}`}
                      onClick={() => toggleReviewType("streetfolio")}
                    >
                      Streetfolio Services
                    </button>
                    {/* )} */}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {loading || services.length > 0 ? (
            <div className="table-responsive sticky-table-container">
              <style>
                {`
                  .sticky-seo-table thead tr:first-child th {
                    position: sticky;
                    top: 0;
                    z-index: 20;
                    background: #f8f9fa;
                   }

                  .sticky-seo-table thead tr:nth-child(2) th {
                    position: sticky;
                    top: 25px; /* Adjust if needed based on first header height */
                    z-index: 19;
                    background: white;
                   }

                  .sticky-table-container {
                    max-height: calc(100vh - 268px); /* Adjust height if needed */
                    overflow: auto;
                   }

                   .sticky-seo-table th {
                    vertical-align: top;
                   }

                  .skeleton-box {
                    height: 20px;
                    background: #f2f2f2;
                    background: linear-gradient(90deg, #f2f2f2 25%, #e6e6e6 50%, #f2f2f2 75%);
                    background-size: 200% 100%;
                    animation: skeleton-loading 1.5s infinite;
                    border-radius: 4px;
                    width: 100%;
                  }

                  @keyframes skeleton-loading {
                    0% { background-position: 200% 0; }
                    100% { background-position: -200% 0; }
                  }
                `}
              </style>
              <table className="table table-condensed table-bordered bg-white sticky-seo-table">
                <thead>
                  <tr>
                    <th style={{ textAlign: "center" }}>#</th>
                    <th>Page Name</th>
                    <th>Title</th>
                    <th>Description</th>
                    <th style={{ width: "1%" }}>Edit</th>
                  </tr>
                  <tr>
                    <th style={{ textAlign: "center" }}></th>
                    <th>
                      <input
                        type="text"
                        className="form-control input-sm"
                        placeholder="Search page..."
                        value={pageNameFilter}
                        onChange={(e) => setPageNameFilter(e.target.value)}
                      />
                    </th>
                    <th>
                      <input
                        type="text"
                        className="form-control input-sm"
                        placeholder="Search title..."
                        value={titleFilter}
                        onChange={(e) => setTitleFilter(e.target.value)}
                      />
                    </th>
                    <th>
                      <input
                        type="text"
                        className="form-control input-sm"
                        placeholder="Search description..."
                        value={descriptionFilter}
                        onChange={(e) => setDescriptionFilter(e.target.value)}
                      />
                    </th>
                    <th>
                      <button
                        className="btn btn-default btn-sm"
                        onClick={() => {
                          setPageNameFilter("");
                          setTitleFilter("");
                          setDescriptionFilter("");
                        }}
                        title="Clear filters"
                      >
                        Clear
                      </button>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {loading || isFiltering ? (
                    Array.from({ length: 15 }).map((_, index) => (
                      <tr key={index} className="skeleton-row">
                        <td className="text-center">
                          <div className="skeleton-box" />
                        </td>
                        <td><div className="skeleton-box" /></td>
                        <td><div className="skeleton-box" /></td>
                        <td><div className="skeleton-box" /></td>
                        <td>
                          <div className="skeleton-box" style={{ width: "30px" }} />
                        </td>
                      </tr>
                    ))
                  ) : paginatedServices.length > 0 ? (
                    paginatedServices.map((service, index) => (
                      <tr key={service.id}>
                        <td
                          style={{
                            textAlign: "center",
                            verticalAlign: "middle",
                          }}
                        >
                          {(page - 1) * limit + index + 1}
                        </td>
                        <td style={{ verticalAlign: "middle" }}>
                          {service.service_name}
                        </td>
                        <td style={{ verticalAlign: "middle" }}>
                          {service.seo_title}
                        </td>
                        <td
                          style={{
                            whiteSpace: "pre-wrap",
                            wordBreak: "break-word",
                            verticalAlign: "middle",
                          }}
                        >
                          {service.seo_desc}
                        </td>
                        <td>
                          {reviewType === "streetgains" && hasPermission("seo_credits", "edit") && (
                            <Link
                              to={`edit/${service.id}`}
                              className="btn btn-warning btn-xs"
                              style={{ float: "left" }}
                              onClick={() => handleEdit(service.id)}
                              title="Edit Service"
                            >
                              <FontAwesomeIcon icon={faPencil} />
                            </Link>
                          )}
                          {reviewType === "streetfolio" && hasPermission("seo_portfolio", "edit") && (
                            <Link
                              to={`edit/${service.id}`}
                              className="btn btn-warning btn-xs"
                              style={{ float: "left" }}
                              onClick={() => handleEdit(service.id)}
                              title="Edit Service"
                            >
                              <FontAwesomeIcon icon={faPencil} />
                            </Link>
                          )}
                        </td>

                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="5" className="text-center">
                        No {reviewType === "streetfolio" ? "Streetfolio" : "Streetgains"} services match your search criteria.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="p-3">
              No {reviewType === "streetfolio" ? "Streetfolio" : "Streetgains"} records found. <Link to={`${addPage}/${reviewType}`}>Add a new service</Link>
            </div>
          )}
        </div>
      </div>

      {/* Modal component */}
      <div id="myModal" className="modal">
        <div className="modal-dialog panel panel-default panel-border top">
          <div className="modal-content">
            <div id="myModalContent"></div>
          </div>
        </div>
      </div>

      {/* Scroll to top */}
      <a href="#" data-click="scroll-top" className="btn-scroll-top fade">
        <i className="ti-arrow-up"></i>
      </a>
    </div>
  );
};

export default ManageServicesSeo;